/**
 * PATIENT-360-V1 (founder MATCH 2026-08-16, ruling D-360d) -- the shared
 * COUNTS-grain stage-ladder render for the ONE patient page (doctor lens +
 * patient record, both role variants).
 *
 * Presentational only: consumes a `CountsStageLadder` derived by THE ONE
 * stage-ladder owner (frontend/src/dashboard/doctor/consult-stage-view.ts --
 * deriveCountsStageLadder) and renders it with the EXISTING badge primitive
 * (AcBadge semantic tones; zero new CSS). Honest by construction: the caller
 * passes null when no lawful ladder exists and this component renders
 * NOTHING -- never a fabricated "Stage 1 of N".
 *
 * Named stages deliberately do NOT render here (the ruled v1 fence: names
 * stay the consult's); the ladder speaks counts -- done / current / planned.
 */

import AcBadge from './AcBadge';
import {
  countsStageLine,
  type CountsStageLadder,
} from '@/dashboard/doctor/consult-stage-view';

export interface JourneyStageLadderProps {
  readonly ladder: CountsStageLadder | null;
  /** Optional course chip (e.g. "Course #93610 - 2 visits"). */
  readonly courseLabel?: string;
  readonly testId?: string;
}

export default function JourneyStageLadder({
  ladder,
  courseLabel,
  testId,
}: JourneyStageLadderProps): JSX.Element | null {
  if (ladder === null) return null;
  return (
    <p className="text-sm" data-testid={testId}>
      <AcBadge tone={ladder.complete ? 'positive' : 'info'}>
        {countsStageLine(ladder)}
      </AcBadge>{' '}
      <span className="text-muted-foreground">
        {ladder.doneCount} done
        {ladder.complete
          ? ''
          : ` - ${ladder.stageTotal - ladder.doneCount} remaining`}
      </span>
      {courseLabel !== undefined && courseLabel !== '' ? (
        <span className="ml-2">
          <AcBadge tone="neutral">{courseLabel}</AcBadge>
        </span>
      ) : null}
    </p>
  );
}
